import { Controller } from "@hotwired/stimulus";

const PREPARATION_SOURCE_FIELD_PATTERNS = [
  /^preparation_cooking_sources_attributes_(\d+)_(.+)$/,
  /^preparation\[cooking_sources_attributes\]\[(\d+)\]\[(.+)\]$/
];
const SNAPSHOT_SOURCE_ATTRIBUTES = [
  "source_kind",
  "title",
  "url",
  "canonical_url",
  "author",
  "site_name",
  "publisher",
  "cookbook_title",
  "page_number",
  "isbn",
  "attribution_text",
  "source_notes",
  "recipe_capture_id",
  "position",
  "recipe_snapshot_source_id"
];

export default class extends Controller {
  static values = { applyUrl: String, workspaceUrl: String };

  static targets = [
    "relationship",
    "pathOption",
    "savedPath",
    "preparationOnlyPath",
    "selectedState",
    "emptyState",
    "conflictState",
    "picker",
    "selection",
    "status",
    "retryCreatedRecipeButton",
    "changeButton",
    "useDishButton",
    "dialog",
    "dialogTitle",
    "dialogMessage",
    "dialogCancel",
    "dialogConfirm",
    "snapshotTemplate"
  ];

  connect() {
    this.currentRequest = null;
    this.pendingRecipeId = null;
    this.pendingRecipeLabel = "";
    this.dialogMode = null;
    this.pickerReturnState = null;
    this.isApplying = false;
    this.suppressSelectionChange = false;
    this.createdRecipeForRetry = null;
    this.workspaceTrigger = null;
    this.currentRecipeId = this.selectionValue() || this.relationshipTarget.dataset.selectedRecipeId?.trim() || "";
    this.currentRecipeLabel = this.selectionLabel() || this.relationshipTarget.dataset.selectedRecipeLabel?.trim() || "";
    this.previousRecipeId = this.currentRecipeId;
    this.form = this.element.closest("form");
    this.boundPreventSubmitWhileApplying = this.preventSubmitWhileApplying.bind(this);
    this.form?.addEventListener("submit", this.boundPreventSubmitWhileApplying, true);
    this.boundRestoreRecipeSnapshot = this.restoreRecipeSnapshot.bind(this);
    this.element.addEventListener("form-draft:restore-recipe-snapshot", this.boundRestoreRecipeSnapshot);
    this.boundRestoredFields = this.syncPathFromRestoredFields.bind(this);
    this.element.addEventListener("form-draft:restored-fields", this.boundRestoredFields);
    this.boundDishSelected = this.syncDishRecipeConflict.bind(this);
    this.element.addEventListener("preparation:dish-selected", this.boundDishSelected);
    this.boundWorkspaceRecipeCreated = this.handleWorkspaceRecipeCreated.bind(this);
    document.addEventListener("preparation-recipe-workspace:created", this.boundWorkspaceRecipeCreated);
    this.showState(this.relationshipTarget.dataset.preparationRecipeState || (this.currentRecipeId ? "selected" : "empty"));
    this.initializePathSelection();
    this.syncDishRecipeConflict();
    this.canonicalRecipeSnapshot = this.currentRecipeId ? this.recipeSnapshotState() : null;
  }

  disconnect() {
    this.currentRequest?.abort();
    this.form?.removeEventListener("submit", this.boundPreventSubmitWhileApplying, true);
    this.element.removeEventListener("form-draft:restore-recipe-snapshot", this.boundRestoreRecipeSnapshot);
    this.element.removeEventListener("form-draft:restored-fields", this.boundRestoredFields);
    this.element.removeEventListener("preparation:dish-selected", this.boundDishSelected);
    document.removeEventListener("preparation-recipe-workspace:created", this.boundWorkspaceRecipeCreated);
  }

  choosePath(event) {
    const radio = event?.target;
    if (radio?.type !== "radio" || !radio.checked) return;

    this.setPathSelection(radio.value);
  }

  initializePathSelection() {
    const selected = this.pathOptionTargets.find((radio) => radio.checked)?.value;
    if (selected) {
      this.setPathSelection(selected);
    } else if (this.currentRecipeId) {
      this.setPathSelection("saved_recipe");
    } else if (this.meaningfulSnapshotExists()) {
      this.setPathSelection("preparation_only");
    } else {
      this.setPathSelection("");
    }
  }

  syncPathFromRestoredFields() {
    if (this.pathOptionTargets.some((radio) => radio.checked)) return;

    this.initializePathSelection();
  }

  setPathSelection(path) {
    this.pathOptionTargets.forEach((radio) => {
      radio.checked = radio.value === path;
    });
    if (this.hasSavedPathTarget) this.setPathContent(this.savedPathTarget, path === "saved_recipe");
    if (this.hasPreparationOnlyPathTarget) this.setPathContent(this.preparationOnlyPathTarget, path === "preparation_only");
  }

  setPathContent(element, visible) {
    element.classList.toggle("hidden", !visible);
    element.setAttribute("aria-hidden", visible ? "false" : "true");
    element.toggleAttribute("inert", !visible);
  }

  handleSelectionChange(event) {
    if (this.suppressSelectionChange || this.isApplying) return;

    const recipeId = String(event.detail && Object.hasOwn(event.detail, "id") ? event.detail.id : this.selectionValue()).trim();
    const recipeLabel = String(event.detail && Object.hasOwn(event.detail, "label") ? event.detail.label : this.selectionLabel()).trim();
    if (!recipeId) {
      if (!this.isPickerOpen()) return;

      if (this.currentRecipeId) {
        this.announce("Your selected saved recipe is unchanged. Choose a recipe or cancel.");
        return;
      }

      this.announce("Search for a saved recipe to continue.");
      return;
    }

    if (recipeId === this.currentRecipeId && !this.isPickerOpen()) return;
    if (recipeId === this.currentRecipeId && this.isPickerOpen()) {
      this.cancelPicker();
      return;
    }

    this.pendingRecipeId = recipeId;
    this.pendingRecipeLabel = recipeLabel;
    if (this.snapshotContentWouldBeReplaced()) {
      this.openConfirmation("apply");
    } else {
      this.applyRecipe(recipeId);
    }
  }

  openPicker(event) {
    event?.preventDefault();
    if (this.isApplying) return;

    this.pickerReturnState = this.relationshipTarget.dataset.preparationRecipeState || "empty";
    // The picker query is candidate state. Keep the committed Recipe ID in the
    // submitted field until a replacement has been successfully applied.
    this.setSearchQuery(this.currentRecipeLabel);
    this.showState("picker");
    window.setTimeout(() => {
      this.relationshipTarget.querySelector("[data-record-autocomplete-target='search']")?.focus();
    }, 0);
  }

  cancelPicker(event) {
    event?.preventDefault();
    if (this.isApplying) return;

    this.pendingRecipeId = null;
    this.setSelection(this.currentRecipeId, this.currentRecipeLabel);
    this.selectionTarget.dispatchEvent(new Event("change", { bubbles: true }));
    const returnState = this.pickerReturnState || (this.currentRecipeId ? "selected" : "empty");
    this.showState(returnState);
    this.pickerReturnState = null;
    this.announce("");
    this.focusStateControl(returnState);
  }

  openCreationWorkspace(event) {
    event?.preventDefault();
    if (this.isApplying) return;

    this.workspaceTrigger = event?.currentTarget || null;
    if (this.currentRecipeId || this.meaningfulSnapshotExists()) {
      this.openConfirmation("create");
    } else {
      this.dispatchWorkspaceOpen();
    }
  }

  dispatchWorkspaceOpen() {
    if (!this.hasWorkspaceUrlValue) return;

    document.dispatchEvent(new CustomEvent("preparation-recipe-workspace:open", {
      detail: {
        trigger: this.workspaceTrigger,
        url: this.workspaceUrlValue
      }
    }));
  }

  handleWorkspaceRecipeCreated(event) {
    const recipeId = String(event.detail?.recipeId || "").trim();
    if (!recipeId || this.isApplying) return;

    const recipeLabel = String(event.detail?.recipeLabel || "").trim();
    this.createdRecipeForRetry = { id: recipeId, label: recipeLabel };
    this.setCandidateSelection(recipeId, recipeLabel);
    this.applyRecipe(recipeId, {
      createdFromWorkspace: true,
      createdRecipeLabel: recipeLabel
    });
  }

  retryCreatedRecipe(event) {
    event?.preventDefault();
    if (!this.createdRecipeForRetry || this.isApplying) return;

    const { id, label } = this.createdRecipeForRetry;
    this.setCandidateSelection(id, label);
    this.applyRecipe(id, {
      createdFromWorkspace: true,
      createdRecipeLabel: label
    });
  }

  setCandidateSelection(id, label) {
    this.suppressSelectionChange = true;
    this.setSelection(id, label);
    this.suppressSelectionChange = false;
    this.pendingRecipeId = id;
    this.pendingRecipeLabel = label;
  }

  openRecipeDetails(event) {
    event?.preventDefault();

    const snapshot = this.element.querySelector("#preparation-recipe-snapshot-fields");
    const section = snapshot?.closest("[data-accordion-section]");
    if (!snapshot || !section) return;

    this.setAccordionSectionOpen(section, true);
    window.setTimeout(() => snapshot.querySelector("[name]:not([type='hidden'])")?.focus(), 0);
  }

  resolveDishConflict(event) {
    event?.preventDefault();
    if (this.isApplying) return;
    if (this.relationshipTarget.dataset.dishSelectionLocked === "true") return;

    const recipeId = this.relationshipTarget.dataset.conflictRecipeId;
    const dishId = this.relationshipTarget.dataset.conflictDishId;
    const dishName = this.relationshipTarget.dataset.conflictDishName;
    if (!recipeId || !dishId) return;

    if (this.currentRecipeId === recipeId) {
      this.dispatchRecipeDishSelection(dishId, dishName);
      return;
    }

    this.applyRecipe(recipeId, { useRecipeDish: true });
  }

  openConfirmation(mode) {
    if (!this.hasDialogTarget) return;

    this.dialogMode = mode;
    if (mode === "remove") {
      this.dialogTitleTarget.textContent = "Remove saved recipe?";
      this.dialogMessageTarget.textContent = "The saved recipe will not be deleted. The recipe details already in this preparation will stay; only the link to the saved recipe will be removed.";
      this.dialogCancelTarget.textContent = "Keep saved recipe";
      this.dialogConfirmTarget.textContent = "Remove saved recipe";
    } else if (mode === "create") {
      this.dialogTitleTarget.textContent = "Create a saved recipe?";
      this.dialogMessageTarget.textContent = "The recipe details currently in this preparation will be replaced by the new saved recipe details. Your date, story, photos, occasions, and other preparation details will stay.";
      this.dialogCancelTarget.textContent = "Keep current details";
      this.dialogConfirmTarget.textContent = "Continue to create recipe";
    } else {
      this.dialogTitleTarget.textContent = "Replace recipe details?";
      this.dialogMessageTarget.textContent = "Using this saved recipe will replace the recipe details in this preparation. Your date, story, photos, and other preparation details will stay.";
      this.dialogCancelTarget.textContent = "Keep current recipe";
      this.dialogConfirmTarget.textContent = "Use saved recipe and replace details";
    }

    if (this.dialogTarget.showModal) {
      this.dialogTarget.showModal();
    } else {
      this.dialogTarget.setAttribute("open", "");
    }
    window.setTimeout(() => this.dialogConfirmTarget.focus(), 0);
  }

  confirmDialog(event) {
    event?.preventDefault();
    const mode = this.dialogMode;
    this.closeDialog();

    if (mode === "create") {
      this.pendingRecipeId = null;
      this.dispatchWorkspaceOpen();
    } else if (mode === "remove") {
      this.removeRecipe();
    } else if (this.pendingRecipeId) {
      this.applyRecipe(this.pendingRecipeId);
    }
  }

  cancelDialog(event) {
    event?.preventDefault();
    const mode = this.dialogMode;
    const workspaceTrigger = this.workspaceTrigger;
    this.closeDialog();
    this.pendingRecipeId = null;
    const returnState = this.pickerReturnState || (this.currentRecipeId ? "selected" : "empty");
    this.setSelection(this.currentRecipeId, this.currentRecipeLabel);
    this.selectionTarget.dispatchEvent(new Event("change", { bubbles: true }));
    this.showState(returnState);
    this.pickerReturnState = null;
    this.announce("");
    if (mode === "create" && workspaceTrigger?.isConnected) {
      window.setTimeout(() => workspaceTrigger.focus(), 0);
    } else {
      this.focusStateControl(returnState);
    }
    if (mode === "create") this.workspaceTrigger = null;
  }

  handleDialogCancel(event) {
    event.preventDefault();
    this.cancelDialog(event);
  }

  removeRecipe() {
    this.previousRecipeId = this.currentRecipeId || this.previousRecipeId;
    this.setSnapshotTemplateRecipeId(this.previousRecipeId);
    this.suppressSelectionChange = true;
    this.setSelection("", "");
    this.suppressSelectionChange = false;
    this.currentRecipeId = "";
    this.currentRecipeLabel = "";
    this.pendingRecipeId = null;
    this.pickerReturnState = null;
    this.relationshipTarget.dataset.conflictRecipeId = "";
    this.relationshipTarget.dataset.preparationRecipeState = "empty";
    this.showState("empty");
    this.selectionTarget.dispatchEvent(new Event("change", { bubbles: true }));
    this.announce("Saved recipe removed. The recipe details in this preparation stay.");
    this.focusStateControl("empty");
  }

  requestRemove(event) {
    event?.preventDefault();
    if (!this.currentRecipeId || this.isApplying) return;

    this.openConfirmation("remove");
  }

  syncDishRecipeConflict() {
    if (this.relationshipTarget.dataset.relationshipEnabled !== "true") return;
    if (!this.currentRecipeId || !this.relationshipTarget.dataset.selectedRecipeDishId) return;

    const dish = this.selectedPreparationDish();
    const recipeDishId = this.relationshipTarget.dataset.selectedRecipeDishId;
    const recipeDishName = this.relationshipTarget.dataset.selectedRecipeDishName || "";
    const mismatch = dish.id
      ? dish.id !== recipeDishId
      : dish.name && this.normalizedDishName(dish.name) !== this.normalizedDishName(recipeDishName);
    if (mismatch) {
      this.relationshipTarget.dataset.conflictRecipeId = this.currentRecipeId;
      this.relationshipTarget.dataset.conflictDishId = recipeDishId;
      this.relationshipTarget.dataset.conflictDishName = recipeDishName;
      if (this.hasUseDishButtonTarget) this.useDishButtonTarget.textContent = "Use recipe's dish";
      this.relationshipTarget.dataset.preparationRecipeState = "conflict";
      this.setPathSelection("saved_recipe");
      this.showState("conflict");
    } else if (this.relationshipTarget.dataset.preparationRecipeState === "conflict") {
      this.relationshipTarget.dataset.conflictRecipeId = "";
      this.relationshipTarget.dataset.conflictDishId = "";
      this.relationshipTarget.dataset.conflictDishName = "";
      if (this.hasUseDishButtonTarget) this.useDishButtonTarget.textContent = "Use recipe's dish and apply recipe";
      this.showState("selected");
    }
  }

  selectedPreparationDish() {
    const dishId = this.element.querySelector("#preparation_dish_id")?.value?.trim() || "";
    const newDishName = this.element.querySelector("#preparation_new_dish_name")?.value?.trim() || "";
    const dishName = this.element.querySelector("#preparation_dish_id")?.dataset.dishName?.trim() || "";
    return { id: dishId, name: newDishName || dishName };
  }

  normalizedDishName(value) {
    return (value || "").trim().replace(/\s+/g, " ").toLowerCase();
  }

  setSearchQuery(query) {
    const search = this.relationshipTarget.querySelector("[data-record-autocomplete-target='search']");
    const clearButton = this.relationshipTarget.querySelector("[data-record-autocomplete-target='clearButton']");
    if (!search) return;

    search.value = query || "";
    search.setAttribute("aria-expanded", "false");
    clearButton?.classList.add("hidden");
  }

  async applyRecipe(recipeId, options = {}) {
    if (!recipeId || !this.hasApplyUrlValue || this.isApplying) return;

    this.isApplying = true;
    this.pendingRecipeId = recipeId;
    this.announce("Applying saved recipe details…");
    const request = new AbortController();
    this.currentRequest = request;

    try {
      const response = await fetch(this.applyUrlValue, {
        method: "POST",
        headers: {
          Accept: "text/html",
          "X-CSRF-Token": this.csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        },
        body: this.requestBody(recipeId, options),
        signal: request.signal
      });
      const html = await response.text();
      if (!response.ok && response.status !== 409) throw new Error(`Recipe application failed with ${response.status}`);

      this.replaceBoundedRegions(html, response.status);
      if (response.status === 409) {
        this.finishConflictState(options);
      } else {
        this.finishSuccessfulApplication(options);
      }
    } catch (error) {
      if (error?.name === "AbortError") return;

      this.isApplying = false;
      this.setSelection(this.currentRecipeId, this.currentRecipeLabel);
      this.pendingRecipeId = null;
      this.pickerReturnState = null;
      this.showState(this.currentRecipeId ? "selected" : "empty");
      if (options.createdFromWorkspace) {
        const label = options.createdRecipeLabel || "the saved recipe";
        this.createdRecipeForRetry = { id: recipeId, label: options.createdRecipeLabel || "" };
        this.showCreatedRecipeRetry();
        if (this.hasRetryCreatedRecipeButtonTarget) this.retryCreatedRecipeButtonTarget.focus();
        this.announce(`${label} was saved, but we couldn't apply it to this preparation. Your current preparation details are unchanged.`);
      } else {
        this.announce("We couldn't apply that saved recipe. Your current preparation details are unchanged.");
      }
      if (options.restoreDraftFields) this.dispatchRecipeSnapshotRestoreFailed();
    } finally {
      if (this.currentRequest === request) this.currentRequest = null;
    }
  }

  requestBody(recipeId, options = {}) {
    const body = new FormData();
    body.append("recipe_id", recipeId);
    const previousRecipeId = this.currentRecipeId || this.previousRecipeId;
    if (previousRecipeId) body.append("previous_recipe_id", previousRecipeId);
    if (options.useRecipeDish) body.append("use_recipe_dish", "1");
    if (options.snapshotOnly) body.append("snapshot_template_restore", "1");

    const fieldRoots = [
      "#preparation-name-field",
      "#preparation_dish_id",
      "#preparation_locked_dish_id",
      "#preparation_new_dish_name",
      "#preparation-recipe-snapshot-fields"
    ];
    const fields = new Set();
    fieldRoots.forEach((selector) => {
      const root = this.element.querySelector(selector);
      if (!root) return;

      if (root.matches("input, textarea, select")) {
        fields.add(root);
      } else {
        root.querySelectorAll("input, textarea, select").forEach((field) => fields.add(field));
      }
    });

    fields.forEach((field) => {
      if (!field.name || field.disabled) return;
      if ((field.type === "checkbox" || field.type === "radio") && !field.checked) return;

      body.append(field.name, field.value);
    });

    if (options.restoreDraftFields) {
      const snapshotSources = this.independentSnapshotSources(options.restoreDraftFields);
      if (snapshotSources.length > 0) body.append("snapshot_template_sources", JSON.stringify(snapshotSources));
    }

    return body;
  }

  independentSnapshotSources(fields) {
    const sources = new Map();

    Object.entries(fields || {}).forEach(([identifier, value]) => {
      const sourceField = this.sourceFieldFromIdentifier(identifier);
      if (!sourceField) return;

      const source = sources.get(sourceField.index) || {};
      source[sourceField.attribute] = value;
      sources.set(sourceField.index, source);
    });

    return Array.from(sources.entries())
      .sort(([firstIndex], [secondIndex]) => Number(firstIndex) - Number(secondIndex))
      .filter(([, source]) => String(source.recipe_snapshot_source_id || "").trim() === "")
      .filter(([, source]) => this.snapshotSourceHasValues(source))
      .map(([index, source]) => this.snapshotSourceAttributes(source, index));
  }

  sourceFieldFromIdentifier(identifier) {
    for (const pattern of PREPARATION_SOURCE_FIELD_PATTERNS) {
      const match = identifier.match(pattern);
      if (match) return { index: Number(match[1]), attribute: match[2] };
    }

    return null;
  }

  snapshotSourceHasValues(source) {
    return SNAPSHOT_SOURCE_ATTRIBUTES.some((attribute) => (
      attribute !== "position" &&
      attribute !== "recipe_snapshot_source_id" &&
      String(source[attribute] || "").trim() !== ""
    ));
  }

  snapshotSourceAttributes(source, index) {
    return SNAPSHOT_SOURCE_ATTRIBUTES.reduce((attributes, attribute) => {
      if (attribute === "recipe_snapshot_source_id") {
        attributes[attribute] = String(source[attribute] || "");
      } else if (attribute === "position") {
        const position = String(source[attribute] || "").trim();
        attributes[attribute] = /^\d+$/.test(position) ? Number(position) : index;
      } else {
        attributes[attribute] = source[attribute] || "";
      }

      return attributes;
    }, {});
  }

  replaceBoundedRegions(html) {
    const parsed = new window.DOMParser().parseFromString(html, "text/html");
    const incomingRelationship = parsed.querySelector("#preparation-recipe-relationship");
    if (!incomingRelationship) throw new Error("Recipe application response did not include the relationship region");

    const accordionStates = [
      [ "#preparation-recipe-snapshot-fields", this.accordionSectionOpenFor("#preparation-recipe-snapshot-fields") ]
    ];
    [
      "preparation-recipe-relationship",
      "preparation-name-field"
    ].forEach((id) => {
      const current = document.getElementById(id);
      const replacement = parsed.getElementById(id);
      if (!current || !replacement) throw new Error(`Recipe application response omitted #${id}`);

      current.replaceWith(document.importNode(replacement, true));
    });

    accordionStates.forEach(([selector, isOpen]) => {
      const section = this.element.querySelector(selector)?.closest("[data-accordion-section]");
      if (section) this.setAccordionSectionOpen(section, isOpen);
    });
  }

  finishConflictState(options = {}) {
    this.isApplying = false;
    this.pendingRecipeId = null;
    this.pickerReturnState = "conflict";
    this.currentRecipeId = this.selectionValue();
    this.currentRecipeLabel = this.selectionLabel();
    this.createdRecipeForRetry = null;
    this.hideCreatedRecipeRetry();
    this.setPathSelection("saved_recipe");
    this.showState("conflict");
    const conflictMessage = this.relationshipTarget.dataset.dishSelectionLocked === "true"
      ? "This preparation's dish is locked. Choose another saved recipe."
      : "The saved recipe uses a different dish. Choose the recipe's dish and apply the recipe, or choose another recipe.";
    this.announce(conflictMessage);
    if (!options.restoreDraftFields) {
      if (this.hasUseDishButtonTarget) this.useDishButtonTarget.focus();
      else this.conflictStateTarget.querySelector("[data-action*='openPicker']")?.focus();
    }
    if (options.restoreDraftFields) this.dispatchRecipeSnapshotRestoreFailed();
  }

  finishSuccessfulApplication(options = {}) {
    const relationship = this.relationshipTarget;
    const selectedRecipeLabel = this.selectionLabel();
    const seededDishId = relationship.dataset.seededDishId;
    const seededDishName = relationship.dataset.seededDishName;
    const selectedRecipeId = this.selectionValue();
    const hasSelectedRecipe = Boolean(selectedRecipeId);

    this.isApplying = false;
    this.currentRecipeId = selectedRecipeId;
    this.currentRecipeLabel = selectedRecipeLabel;
    this.canonicalRecipeSnapshot = hasSelectedRecipe ? this.recipeSnapshotState() : null;
    this.createdRecipeForRetry = null;
    this.previousRecipeId = this.currentRecipeId;
    this.pendingRecipeId = null;
    this.pickerReturnState = null;
    if (!options.snapshotOnly) this.setSnapshotTemplateRecipeId("");
    this.relationshipTarget.dataset.preparationRecipeState = hasSelectedRecipe ? "selected" : "empty";
    if (hasSelectedRecipe) this.setPathSelection("saved_recipe");
    this.closeRecipeDetails();
    this.showState(hasSelectedRecipe ? "selected" : "empty");
    this.hideCreatedRecipeRetry();
    if (seededDishId) this.dispatchRecipeDishSelection(seededDishId, seededDishName);
    if (options.restoreDraftFields) {
      this.element.dispatchEvent(new CustomEvent("form-draft:apply-recipe-snapshot-fields", {
        bubbles: true,
        detail: { fields: options.restoreDraftFields, snapshotOnly: options.snapshotOnly === true }
      }));
    }
    this.element.dispatchEvent(new CustomEvent("form-draft:persist-current-snapshot", { bubbles: true }));
    this.announce(
      hasSelectedRecipe
        ? "Saved recipe selected."
        : "Saved recipe link removed. The recipe details stay in this preparation.",
      { visuallyHidden: hasSelectedRecipe }
    );
    if (options.restoreDraftFields) return;

    if (hasSelectedRecipe && this.hasChangeButtonTarget) this.changeButtonTarget.focus();
    else this.focusStateControl("empty");
  }

  restoreRecipeSnapshot(event) {
    const recipeId = event.detail?.recipeId;
    const fields = event.detail?.fields;
    if (!recipeId || !fields) return;

    this.setPathSelection("saved_recipe");

    this.applyRecipe(recipeId, {
      restoreDraftFields: fields,
      snapshotOnly: event.detail?.snapshotOnly === true
    });
  }

  dispatchRecipeSnapshotRestoreFailed() {
    this.element.dispatchEvent(new CustomEvent("form-draft:recipe-snapshot-restore-failed", {
      bubbles: true
    }));
  }

  setSnapshotTemplateRecipeId(recipeId) {
    if (!this.hasSnapshotTemplateTarget) return;

    const value = String(recipeId || "");
    if (this.snapshotTemplateTarget.value === value) return;

    this.snapshotTemplateTarget.value = value;
    this.snapshotTemplateTarget.dispatchEvent(new Event("input", { bubbles: true }));
    this.snapshotTemplateTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  closeRecipeDetails() {
    const section = this.element.querySelector("#preparation-recipe-snapshot-fields")?.closest("[data-accordion-section]");
    if (section) this.setAccordionSectionOpen(section, false);
  }

  dispatchRecipeDishSelection(id, name) {
    this.element.dispatchEvent(new CustomEvent("preparation:recipe-dish-selected", {
      bubbles: true,
      detail: { id, name }
    }));
  }

  showState(state) {
    const visibility = {
      selected: this.hasSelectedStateTarget ? this.selectedStateTarget : null,
      empty: this.hasEmptyStateTarget ? this.emptyStateTarget : null,
      conflict: this.hasConflictStateTarget ? this.conflictStateTarget : null,
      picker: this.hasPickerTarget ? this.pickerTarget : null
    };

    [ "selected", "empty", "conflict", "picker" ].forEach((name) => {
      const target = visibility[name];
      if (!target) return;

      const visible = name === state;
      target.classList.toggle("hidden", !visible);
      target.setAttribute("aria-hidden", visible ? "false" : "true");
    });
    this.relationshipTarget.dataset.preparationRecipeState = state;
  }

  focusStateControl(state) {
    if (state === "selected" && this.hasChangeButtonTarget) this.changeButtonTarget.focus();
    if (state === "empty") this.relationshipTarget.querySelector("[data-action*='openPicker']")?.focus();
    if (state === "conflict") {
      if (this.hasUseDishButtonTarget) this.useDishButtonTarget.focus();
      else this.conflictStateTarget.querySelector("[data-action*='openPicker']")?.focus();
    }
  }

  preventSubmitWhileApplying(event) {
    if (!this.isApplying) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.announce("Please wait for the saved recipe to finish applying before saving this preparation.");
  }

  setSelection(id, label) {
    if (!this.hasSelectionTarget) return;

    const shell = this.selectionTarget.closest("[data-controller~='record-autocomplete']");
    const search = shell?.querySelector("[data-record-autocomplete-target='search']");
    const clearButton = shell?.querySelector("[data-record-autocomplete-target='clearButton']");
    const suggestions = shell?.querySelector("[data-record-autocomplete-target='suggestions']");

    this.selectionTarget.value = id || "";
    this.selectionTarget.dataset.recordLabel = label || "";
    if (shell) shell.dataset.recordAutocompleteSelectedLabel = label || "";
    if (search) {
      search.value = label || "";
      search.setAttribute("aria-expanded", "false");
    }
    if (clearButton) clearButton.classList.toggle("hidden", !id);
    suggestions?.classList.add("hidden");
  }

  selectionValue() {
    return this.hasSelectionTarget ? this.selectionTarget.value.toString().trim() : "";
  }

  selectionLabel() {
    if (!this.hasSelectionTarget) return "";

    const shell = this.selectionTarget.closest("[data-controller~='record-autocomplete']");
    return (shell?.dataset.recordAutocompleteSelectedLabel ||
      shell?.querySelector("[data-record-autocomplete-target='search']")?.value || "").trim();
  }

  isPickerOpen() {
    return this.relationshipTarget.dataset.preparationRecipeState === "picker";
  }

  meaningfulSnapshotExists() {
    const roots = [ "#preparation-recipe-snapshot-fields" ];
    return roots.some((selector) => {
      const root = this.element.querySelector(selector);
      if (!root) return false;

      return Array.from(root.querySelectorAll("input, textarea, select")).some((field) => {
        if (field.disabled || field.type === "hidden" || field.type === "checkbox" || field.type === "radio") return false;
        if (field.name.includes("position") || field.name.endsWith("[_destroy]")) return false;

        return field.value.toString().trim() !== "";
      });
    });
  }

  hasPreparationSpecificSnapshotChanges() {
    if (!this.canonicalRecipeSnapshot) return this.meaningfulSnapshotExists();

    return JSON.stringify(this.recipeSnapshotState()) !== JSON.stringify(this.canonicalRecipeSnapshot);
  }

  snapshotContentWouldBeReplaced() {
    if (this.currentRecipeId) return this.hasPreparationSpecificSnapshotChanges();

    return Object.keys(this.recipeSnapshotState()).length > 0;
  }

  recipeSnapshotState() {
    const root = this.element.querySelector("#preparation-recipe-snapshot-fields");
    if (!root) return {};

    const fields = Array.from(root.querySelectorAll("input, textarea, select"));
    const recipeSourceRows = new Set();
    fields.forEach((field) => {
      const match = field.name.match(/\[cooking_sources_attributes\]\[(\d+)\]\[recipe_snapshot_source_id\]$/);
      if (match && field.value.trim()) recipeSourceRows.add(match[1]);
    });

    const state = {};
    fields.forEach((field) => {
      if (!field.name || field.disabled || field.type === "hidden" || field.type === "radio") return;
      if (field.closest("[data-sortable-list-target='item'], [data-recipe-section]")) return;

      const sourceMatch = field.name.match(/\[cooking_sources_attributes\]\[(\d+)\]\[(.+)\]$/);
      if (sourceMatch && !recipeSourceRows.has(sourceMatch[1])) return;
      if (field.name.endsWith("[_destroy]")) {
        if (field.type === "checkbox" && field.checked) state[field.name] = field.value;
        return;
      }
      if (field.type === "checkbox" || field.name.includes("position") || field.name.endsWith("[recipe_snapshot_source_id]")) return;

      const value = field.value.toString().trim();
      if (value) state[field.name] = value;
    });

    const ingredients = this.effectiveRowsIn(root.querySelector("[data-dish-holiday-form-target='ingredientsContainer']"));
    const steps = this.effectiveRowsIn(root.querySelector("[data-dish-holiday-form-target='stepsContainer']"));
    const sections = Array.from(root.querySelectorAll("[data-recipe-section]"))
      .filter((section) => section.querySelector("[data-recipe-section-destroy]")?.value !== "1")
      .map((section) => this.effectiveSectionState(section))
      .filter((section) => Object.keys(section).length > 0);
    if (ingredients.length > 0) state.ingredients = ingredients;
    if (steps.length > 0) state.steps = steps;
    if (sections.length > 0) state.sections = sections;

    return Object.fromEntries(Object.entries(state).sort(([left], [right]) => left.localeCompare(right)));
  }

  effectiveRowsIn(container) {
    if (!container) return [];

    return Array.from(container.querySelectorAll("[data-sortable-list-target='item']"))
      .filter((row) => row.closest("[data-controller~='sortable-list']") === container)
      .filter((row) => !this.isSnapshotRowDestroyed(row))
      .map((row) => this.effectiveRowState(row))
      .filter((row) => Object.keys(row).length > 0);
  }

  effectiveRowState(row) {
    const state = {};
    Array.from(row.querySelectorAll("input, textarea, select"))
      .filter((field) => field.closest("[data-sortable-list-target='item']") === row)
      .forEach((field) => {
        if (!field.name || field.disabled || field.type === "hidden" || field.type === "checkbox" || field.type === "radio") return;
        const attribute = field.name.match(/\[([^\]]+)\]$/)?.[1];
        if (!attribute || attribute === "position" || attribute === "id" || attribute === "_destroy" || attribute === "recipe_snapshot_source_id") return;

        const value = field.value.toString().trim();
        if (value) state[attribute] = value;
      });

    return Object.fromEntries(Object.entries(state).sort(([left], [right]) => left.localeCompare(right)));
  }

  isSnapshotRowDestroyed(row) {
    return row.querySelector("input[type='checkbox'][name$='[_destroy]']")?.checked === true;
  }

  effectiveSectionState(section) {
    const state = {};
    Array.from(section.querySelectorAll("input, textarea, select"))
      .filter((field) => field.closest("[data-recipe-section]") === section && !field.closest("[data-sortable-list-target='item']"))
      .forEach((field) => {
        if (!field.name || field.disabled || field.type === "hidden" || field.type === "checkbox" || field.type === "radio") return;
        const attribute = field.name.match(/\[([^\]]+)\]$/)?.[1];
        if (!attribute || attribute === "position" || attribute === "id" || attribute === "_destroy") return;

        const value = field.value.toString().trim();
        if (value) state[attribute] = value;
      });

    const ingredients = this.effectiveRowsIn(section.querySelector("[data-recipe-section-ingredients-container]"));
    const steps = this.effectiveRowsIn(section.querySelector("[data-recipe-section-steps-container]"));
    if (ingredients.length > 0) state.ingredients = ingredients;
    if (steps.length > 0) state.steps = steps;
    return Object.fromEntries(Object.entries(state).sort(([left], [right]) => left.localeCompare(right)));
  }

  accordionSectionOpenFor(selector) {
    return this.element.querySelector(selector)?.closest("[data-accordion-section]")?.hasAttribute("open") || false;
  }

  setAccordionSectionOpen(section, isOpen) {
    if (isOpen) section.setAttribute("open", "");
    else section.removeAttribute("open");

    const trigger = section.querySelector(":scope > [data-accordion-trigger]");
    trigger?.setAttribute("aria-expanded", String(isOpen));
    Array.from(section.children).forEach((child) => {
      if (child === trigger) return;
      child.classList.toggle("hidden", !isOpen);
    });
  }

  announce(message, { visuallyHidden = false } = {}) {
    if (!this.hasStatusTarget) return;

    this.statusTarget.classList.toggle("sr-only", visuallyHidden);
    this.statusTarget.textContent = message;
  }

  showCreatedRecipeRetry() {
    if (!this.hasRetryCreatedRecipeButtonTarget) return;

    this.retryCreatedRecipeButtonTarget.classList.remove("hidden");
  }

  hideCreatedRecipeRetry() {
    if (!this.hasRetryCreatedRecipeButtonTarget) return;

    this.retryCreatedRecipeButtonTarget.classList.add("hidden");
  }

  closeDialog() {
    if (!this.hasDialogTarget) return;

    if (this.dialogTarget.open && this.dialogTarget.close) this.dialogTarget.close();
    else this.dialogTarget.removeAttribute("open");
    this.dialogMode = null;
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || "";
  }
}
